import { degrees, PDFDocument } from "pdf-lib";
import { File } from "../../models/File";

export const useFile = () => {
  const normalizeBase64 = (b64: string) =>
    b64?.startsWith("data:") ? (b64.split(",")[1] ?? "") : b64;

  const looksLikePdf = (b64: string) => b64.startsWith("JVBERi0");
  const looksLikeJpg = (b64: string) => b64.startsWith("/9j/");
  const looksLikePng = (b64: string) => b64.startsWith("iVBOR");

  async function ensurePdfBase64(file: File): Promise<string> {
    const base64 = normalizeBase64(file.base64);

    // If bytes are already PDF -> return
    if (looksLikePdf(base64)) return base64;

    // If bytes look like image -> convert
    if (looksLikeJpg(base64) || looksLikePng(base64) || file.mimeType?.startsWith("image/")) {
      const pdfDoc = await PDFDocument.create();

      const mime = looksLikePng(base64) || file.mimeType === "image/png"
        ? "image/png"
        : "image/jpeg";

      const dataUrl = `data:${mime};base64,${base64}`;
      const imgBytes = await fetch(dataUrl).then((r) => r.arrayBuffer());

      const embedded =
        mime === "image/png"
          ? await pdfDoc.embedPng(imgBytes)
          : await pdfDoc.embedJpg(imgBytes);

      const page = pdfDoc.addPage([embedded.width, embedded.height]);
      page.drawImage(embedded, { x: 0, y: 0, width: embedded.width, height: embedded.height });

      const pdfBytes = await pdfDoc.save();

      let bin = "";
      const bytes = new Uint8Array(pdfBytes);
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      const out = btoa(bin);

      if (!looksLikePdf(out)) throw new Error("Image->PDF conversion failed");
      return out;
    }

    throw new Error("Unsupported file type");
  }

  async function saveEditedPdf({
    file,
    canvasRefs,
    extraPages,
    removedPages,
  }: {
    file: File;
    canvasRefs: React.MutableRefObject<Record<number, HTMLCanvasElement | null>>;
    extraPages: number;
    removedPages: Set<number>;
  }) {
    // ✅ always normalize; prevents dataURL issues
    const pdfBase64 = normalizeBase64(file.base64);

    if (!looksLikePdf(pdfBase64)) {
      throw new Error("saveEditedPdf received non-PDF base64");
    }

    const existingPdfBytes = Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0));
    const pdfDoc = await PDFDocument.load(existingPdfBytes);

    const originalPageCount = pdfDoc.getPageCount();
    const totalPages = originalPageCount + extraPages;

    const refPage =
      originalPageCount > 0 ? pdfDoc.getPage(originalPageCount - 1) : null;

    const pagesToRemove = [...removedPages]
      .filter((i) => i < originalPageCount)
      .sort((a, b) => b - a);

    for (const i of pagesToRemove) pdfDoc.removePage(i);

    let workingPageIndex = 0;

    for (let i = 0; i < totalPages; i++) {
      if (removedPages.has(i)) continue;

      const canvas = canvasRefs.current[i];

      let page;
      if (workingPageIndex < pdfDoc.getPageCount()) {
        page = pdfDoc.getPage(workingPageIndex);
      } else {
        if (!refPage) {
          page = pdfDoc.addPage();
        } else {
          const { width, height } = refPage.getSize();
          const rotation = refPage.getRotation().angle;

          page = pdfDoc.addPage([width, height]);
          if (rotation !== 0) page.setRotation(degrees(rotation));
        }
      }

      if (canvas && !isCanvasEmpty(canvas)) {
        const pngDataUrl = canvas.toDataURL("image/png");
        const pngImage = await pdfDoc.embedPng(pngDataUrl);

        const { width, height } = page.getSize();
        page.drawImage(pngImage, { x: 0, y: 0, width, height });
      }

      workingPageIndex++;
    }

    const pdfBytes = await pdfDoc.save();

    let bin = "";
    const bytes = new Uint8Array(pdfBytes);
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    const out = btoa(bin);

    if (!looksLikePdf(out)) throw new Error("saveEditedPdf produced non-PDF base64");
    return out; // ✅ RAW base64 ONLY
  }

  const isCanvasEmpty = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return true;
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return !data.some((v) => v !== 0);
  };

  return { saveEditedPdf, ensurePdfBase64, normalizeBase64 };
};